import { ApiTranslation, ComplexMapFieldValue, DottedKey } from '@stylehaven/components';
import { isSimpleType } from '../../common/entities/common/simple-type.entity';

export interface WebserviceConfigJson {
    Url: string;
    Login?: string;
    Password?: string;
    // Webservice searchfield key for performing default search. Default WebserviceField key if webservice not searchable.
    GlobalSearchField: string;
    SearchableFields?: SearchableField[];
    Fields: WebserviceField[];
}

export interface SearchableField {
    Key: string; // Searchable key in webservice
    Translations?: ApiTranslation[];
}

export interface PartialWebserviceField {
    FieldDefinitionDottedKey: DottedKey; // FieldDefinition to map to
    Children?: PartialWebserviceField[];
}

export interface WebserviceField extends PartialWebserviceField {
    Key: string; // Name in Webservice
    ShowColumn: boolean; // Show data in column of search dialog
    IsLabel?: boolean; // Mark field to use as label of object
    IsId?: boolean; // Mark field to use as ID of object
}

export class WebserviceConfig {
    private readonly config: WebserviceConfigJson;
    private labelFieldKeys: string[];
    private idFieldKey: string;

    constructor(config: WebserviceConfigJson) {
        this.config = config;
        this.labelFieldKeys = this.initLabelFieldKeys();
        this.idFieldKey = this.initIdFieldKey();
    }

    public get url(): string {
        return this.config.Url;
    }

    public get login(): string {
        return this.config.Login ?? '';
    }

    public get password(): string {
        return this.config.Password ?? '';
    }

    public get globalSearchField(): string {
        return this.config.GlobalSearchField;
    }

    public get searchableFields(): SearchableField[] {
        return this.config.SearchableFields ?? [];
    }

    public get fields(): WebserviceField[] {
        return this.config.Fields;
    }

    public get isSearchable(): boolean {
        return !!this.config.SearchableFields && this.config.SearchableFields.length > 0;
    }

    private initLabelFieldKeys(): string[] {
        const labelFieldFlatKeys: string[] = this.config.Fields.filter(field => field.IsLabel)
            .map(field => field.FieldDefinitionDottedKey)
            .map(dottedKey => dottedKey.split('.').pop())
            .filter((labelPartKey): labelPartKey is string => !!labelPartKey);

        if (!labelFieldFlatKeys.length) {
            console.error(`WebserviceConfig:: No webservice-field marked as label`);
        }

        return labelFieldFlatKeys;
    }

    private initIdFieldKey(): string {
        const idFieldFlatKeys: string[] = this.config.Fields.filter(field => field.IsId)
            .map(field => field.FieldDefinitionDottedKey)
            .map(dottedKey => dottedKey.split('.').pop())
            .filter((flatKey): flatKey is string => !!flatKey);

        if (!idFieldFlatKeys.length) {
            console.error(`WebserviceConfig:: No webservice-field marked as id`);
        }
        return idFieldFlatKeys[0];
    }

    public getWebserviceDataLabel(metadata: ComplexMapFieldValue, noLabelMsg: string): string {
        if (isSimpleType(metadata)) {
            return metadata?.toString() ?? noLabelMsg;
        }

        if (!this.labelFieldKeys) {
            return noLabelMsg;
        }

        const labels: string[] = this.labelFieldKeys
            .map(labelPartKey => metadata[labelPartKey]?.toString())
            .filter((labelPart): labelPart is string => !!labelPart);

        return labels.join(' ') ?? noLabelMsg;
    }

    public getWebserviceDataId(metadata: ComplexMapFieldValue, noIdMessage: string = 'No ID'): string {
        if (isSimpleType(metadata)) {
            return metadata?.toString() ?? noIdMessage;
        }

        return metadata[this.idFieldKey]?.toString() ?? noIdMessage;
    }
}
